import { GAME_METADATA, GAMEPLAY_CONFIG, isBossWave } from '@brick-rogue/game-content';
import { CYBERPUNK_VISUAL_ASSETS } from '@brick-rogue/game-content/visual-assets';
import { useNavigate } from 'react-router';

import gameCoverUrl from '../../assets/game-cover.jpg';
import { AppHeader } from '../../app/AppHeader';
import { useReducedMotionPreference } from '../settings/uiPreferencesStore';
import { useCampaignProgressStore } from './campaignProgressStore';
import { useSaveStore } from '../save/saveStore';

const LEVELS = Array.from({ length: GAMEPLAY_CONFIG.run.maxWaves }, (_, index) => index + 1);

export function LevelSelectPage() {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotionPreference();
  const highestCompletedLevel = useCampaignProgressStore((state) => state.highestCompletedLevel);
  const selectedLevel = useCampaignProgressStore((state) => state.selectedLevel);
  const selectLevel = useCampaignProgressStore((state) => state.selectLevel);
  const saveStatus = useSaveStore((state) => state.status);
  const saveMessage = useSaveStore((state) => state.message);
  const career = useSaveStore((state) => state.save.career);
  const highestUnlockedLevel = Math.min(GAMEPLAY_CONFIG.run.maxWaves, highestCompletedLevel + 1);
  const progressPercent = (highestCompletedLevel / GAMEPLAY_CONFIG.run.maxWaves) * 100;

  const startLevel = (level: number) => {
    if (selectLevel(level)) {
      void navigate('/play');
    }
  };

  return (
    <div
      className="portal-shell level-select-shell"
      data-motion={reducedMotion ? 'reduced' : 'full'}
    >
      <a className="skip-link" href="#level-grid">
        跳到关卡列表
      </a>
      <div className="portal-ambient portal-ambient--one" />
      <div className="portal-ambient portal-ambient--two" />
      <AppHeader compact />

      <main className="level-select-main">
        <section className="campaign-cover" aria-labelledby="campaign-title">
          <img
            className="campaign-cover__image"
            src={gameCoverUrl}
            alt="霓虹弹丸穿过砖块阵列，迎战机械奇点守卫"
            fetchPriority="high"
          />
          <div className="campaign-cover__shade" />
          <div className="campaign-cover__content">
            <p className="section-kicker">30 LEVEL CAMPAIGN</p>
            <h1 id="campaign-title" aria-label={GAME_METADATA.title}>
              <span>Rogue Brick</span>
              <span>Breaker</span>
            </h1>
            <p>逐关突破霓虹防线。完成当前关卡后，下一枚节点才会开放。</p>
            {saveMessage ? (
              <p className="campaign-save-notice" data-save-status={saveStatus} role="status">
                <span aria-hidden="true">◆</span>
                {saveMessage}
              </p>
            ) : null}
            <div className="campaign-cover__actions">
              <button
                className="portal-button portal-button--primary"
                type="button"
                onClick={() => startLevel(highestUnlockedLevel)}
              >
                {highestCompletedLevel >= GAMEPLAY_CONFIG.run.maxWaves
                  ? '重返第 30 关'
                  : `继续第 ${highestUnlockedLevel} 关`}
                <span aria-hidden="true">→</span>
              </button>
              <span>
                已完成 {highestCompletedLevel}/{GAMEPLAY_CONFIG.run.maxWaves}
              </span>
            </div>
            <div
              className="campaign-cover__progress"
              role="progressbar"
              aria-label="战役完成进度"
              aria-valuemin={0}
              aria-valuemax={GAMEPLAY_CONFIG.run.maxWaves}
              aria-valuenow={highestCompletedLevel}
            >
              <i style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </section>

        <section className="campaign-career" aria-label="生涯档案">
          <div>
            <small>行动次数</small>
            <strong>{career.totalRuns}</strong>
          </div>
          <div>
            <small>最高分</small>
            <strong>{career.bestScore.toLocaleString('zh-CN')}</strong>
          </div>
          <div>
            <small>累计破砖</small>
            <strong>{career.totalBricksDestroyed.toLocaleString('zh-CN')}</strong>
          </div>
          <div>
            <small>战斗时长</small>
            <strong>{formatCareerDuration(career.totalPlaySeconds)}</strong>
          </div>
        </section>

        <section className="level-selection" aria-labelledby="level-selection-title">
          <div className="section-heading">
            <div>
              <p className="section-kicker">SELECT CHECKPOINT</p>
              <h2 id="level-selection-title">选择关卡</h2>
            </div>
            <p>已完成关卡可以重复挑战；锁定节点无法提前进入。</p>
          </div>

          <ol id="level-grid" className="level-grid" aria-label="30 个战役关卡">
            {LEVELS.map((level) => {
              const isBossLevel = isBossWave(level);
              const isCompleted = level <= highestCompletedLevel;
              const isLocked = level > highestUnlockedLevel;
              const isSelected = level === selectedLevel;
              const stateLabel = isLocked ? '未解锁' : isCompleted ? '已完成' : '可挑战';
              const bossLabel = isBossLevel ? '，Boss 关卡' : '';
              const bossImageUrl = resolveBossImageUrl(level);

              return (
                <li key={level}>
                  <button
                    className="level-card"
                    type="button"
                    data-level={level}
                    data-boss={String(isBossLevel)}
                    data-completed={String(isCompleted)}
                    data-locked={String(isLocked)}
                    data-selected={String(isSelected)}
                    disabled={isLocked}
                    aria-label={`第 ${level} 关${bossLabel}，${stateLabel}`}
                    onClick={() => startLevel(level)}
                  >
                    <span className="level-card__icon" aria-hidden="true">
                      {bossImageUrl ? (
                        <img src={bossImageUrl} alt="" />
                      ) : (
                        <span className="level-card__beacon">
                          <i />
                          <i />
                        </span>
                      )}
                      <strong>{level.toString().padStart(2, '0')}</strong>
                      {isLocked ? <LockIcon /> : null}
                      {isCompleted ? <span className="level-card__check">✓</span> : null}
                    </span>
                    <span className="level-card__meta">
                      <small>
                        {isBossLevel ? 'BOSS' : `LEVEL ${level.toString().padStart(2, '0')}`}
                      </small>
                      <strong>{stateLabel}</strong>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      </main>

      <footer className="site-footer">
        <span>关卡按顺序解锁 · 进度自动保存</span>
        <span>第 10 / 20 / 30 关 · BOSS 节点</span>
      </footer>
    </div>
  );
}

function resolveBossImageUrl(level: number): string | null {
  if (level === 10 || level === 20 || level === 30) {
    return CYBERPUNK_VISUAL_ASSETS.gameplay.bosses[level];
  }
  return null;
}

function formatCareerDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function LockIcon() {
  return (
    <svg className="level-card__lock" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10" />
      <rect x="5" y="10" width="14" height="11" rx="3" />
      <path d="M12 14v3" />
    </svg>
  );
}
